// Replaces Turbo's default window.confirm() — the native OS dialog that breaks
// the dark theme — with a single design-v2 styled <dialog>. Registered once on
// import (from application.js), so EVERY `data-turbo-confirm` across the app
// (Remove carrier, Delete, Disconnect, …) gets the same on-brand prompt.
//
// Colors come from the design-v2 CSS tokens with dark fallbacks, so it matches
// exactly on design-v2 pages (tokens resolve) and still looks right on any
// remaining legacy page (the fallbacks apply).

import { Turbo } from "@hotwired/turbo-rails"

// Destructive verbs → red confirm button + Cancel focused by default. Matches
// the turbo_confirm copy used across the app.
const DESTRUCTIVE_RE =
  /\b(remove|delete|disconnect|discard|deactivate|destroy|void|reset|archive|unassign|revoke|cancel|clear)\b/i

let dialogEl, msgEl, okBtn, cancelBtn, resolver

function build() {
  const style = document.createElement("style")
  style.textContent = `
    dialog.ws-confirm{border:1px solid var(--line,#2d2f37);background:var(--paper,#1c1e24);
      color:var(--ink,#f0f1f4);border-radius:14px;padding:0;width:min(400px,calc(100vw - 32px));
      box-shadow:0 18px 48px rgba(0,0,0,.5);font-family:inherit}
    dialog.ws-confirm::backdrop{background:rgba(0,0,0,.5)}
    .ws-confirm__msg{margin:0;padding:22px 22px 6px;font-size:15px;font-weight:600;line-height:1.5;color:var(--ink,#f0f1f4)}
    .ws-confirm__act{display:flex;justify-content:flex-end;gap:10px;padding:16px 20px 18px}
    .ws-confirm__act button{font:inherit;font-size:13.5px;font-weight:500;padding:8px 16px;border-radius:9px;
      cursor:pointer;border:1px solid transparent;transition:background .12s,filter .12s}
    .ws-confirm__cancel{background:transparent;border-color:var(--line,#2d2f37);color:var(--ink-2,#b3b6bf)}
    .ws-confirm__cancel:hover{background:var(--paper-2,#22242b)}
    .ws-confirm__ok{background:var(--accent,#6b8aff);color:var(--accent-ink,#12131c)}
    .ws-confirm__ok:hover{filter:brightness(1.08)}
    dialog.ws-confirm.is-danger .ws-confirm__ok{background:var(--bad,#f2706b);color:#fff}
  `
  document.head.appendChild(style)

  dialogEl = document.createElement("dialog")
  dialogEl.className = "ws-confirm"
  dialogEl.setAttribute("role", "alertdialog")
  dialogEl.innerHTML = `
    <p class="ws-confirm__msg"></p>
    <div class="ws-confirm__act">
      <button type="button" class="ws-confirm__cancel">Cancel</button>
      <button type="button" class="ws-confirm__ok">Confirm</button>
    </div>`
  msgEl     = dialogEl.querySelector(".ws-confirm__msg")
  cancelBtn = dialogEl.querySelector(".ws-confirm__cancel")
  okBtn     = dialogEl.querySelector(".ws-confirm__ok")

  cancelBtn.addEventListener("click", () => finish(false))
  okBtn.addEventListener("click", () => finish(true))
  dialogEl.addEventListener("cancel", (e) => { e.preventDefault(); finish(false) }) // ESC key
  dialogEl.addEventListener("click", (e) => { if (e.target === dialogEl) finish(false) }) // backdrop click

  document.body.appendChild(dialogEl)
}

function finish(result) {
  if (dialogEl && dialogEl.open) dialogEl.close()
  const r = resolver
  resolver = null
  if (r) r(result)
}

// Turbo 8 calls this with (message, formElement, submitter). Links pass the
// <a> as formElement with no submitter, so fall back to formElement.
function confirm(message, formElement, submitter) {
  if (!dialogEl) build()

  const text = (message && String(message).trim()) || "Are you sure?"
  msgEl.textContent = text

  const trigger = submitter || formElement
  const danger =
    DESTRUCTIVE_RE.test(text) ||
    !!(trigger && trigger.closest && trigger.closest(".btn-danger, [data-turbo-confirm-danger]"))
  dialogEl.classList.toggle("is-danger", danger)

  return new Promise((resolve) => {
    resolver = resolve
    dialogEl.showModal()
    // Default focus to the safe choice on destructive prompts.
    ;(danger ? cancelBtn : okBtn).focus()
  })
}

Turbo.setConfirmMethod(confirm)
